import React from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical } from 'lucide-react'
import CareerItem from 'components/career-item/career-item.component'
import { IEndUserCareer } from 'interfaces/CareerInterface'
import { noPreviewImage } from 'utills/image'
import { getCareerStatusText } from 'utills/career-status'

type SortableCareerCardProps = {
  item: IEndUserCareer
  rank: number
  reorderMode: boolean
  onClick: () => void
  containerWidth?: number
}

const SortableCareerCard: React.FC<SortableCareerCardProps> = ({
  item,
  rank,
  reorderMode,
  onClick,
  containerWidth = 100,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id })

  const style: React.CSSProperties = {
    // While this card is the active drag we render a floating ghost via
    // DragOverlay. Suppressing the sortable transform keeps the source card
    // pinned in its grid cell so cross-section drags don't make it appear to
    // "disappear" when the cursor leaves the source SortableContext.
    transform: isDragging ? undefined : CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  }

  const dragProps = reorderMode ? { ...attributes, ...listeners } : {}

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...dragProps}
      className={`flex items-start gap-1 ${
        reorderMode ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
    >
      {reorderMode && (
        <span
          className="text-gray-medium mt-2 pointer-events-none"
          aria-hidden="true"
        >
          <GripVertical size={16} />
        </span>
      )}
      <CareerItem
        id={item.id}
        name={item.name}
        icon_url={item.icon_url || noPreviewImage}
        rank={rank}
        showRanking
        showStatus={!reorderMode}
        statusText={getCareerStatusText(item)}
        pressed={reorderMode ? () => undefined : onClick}
        containerHeight={150}
        containerWidth={containerWidth}
        height={72}
        width={72}
        fontSize="body-sm-emphasis"
      />
    </div>
  )
}

export default SortableCareerCard
